import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { createReadableClass, getColorWithAlpha } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import SimpleBar from 'simplebar-react';
import { getAnimation } from '../../../helpers/getAnimation';
import { StyledFreeLink, useTheme } from '../../common';
import { useSource } from '../../sources';
import { usePlayer } from '../PlayerProvider';
import { PlaylistItem } from './PlaylistItem';
import 'simplebar-react/dist/simplebar.min.css';

type HideDirection = 'toRight' | 'toTop';

interface PlaylistProps {
  maxHeight: string | number;
  hideDirection?: HideDirection;
  className?: string;
}

export const Playlist = forwardRef<HTMLDivElement, PlaylistProps>(
  ({ className, maxHeight, hideDirection }, ref) => {
    const { textColor } = useTheme();
    const { layout } = useSettings();
    const { items } = useSource();
    const { showPlayer, showPlaylist } = usePlayer();

    const calculatedHeight = !['100%', 'auto'].includes(
      maxHeight.toString().toLowerCase()
    )
      ? parseInt(maxHeight.toString(), 10)
      : '100%';

    const inBackground = ['floatingBackground', 'embedBackground'].includes(
      layout
    );

    if (!showPlayer || !items?.length) {
      return null;
    }

    return (
      <Component
        ref={ref}
        className={className}
        _showPlaylist={showPlaylist}
        _hideDirection={hideDirection}
        _isEmbedBackground={layout === 'embedBackground'}
      >
        <Container _hideDirection={hideDirection} _showPlaylist={showPlaylist}>
          <ScrollContainer
            autoHide={false}
            _maxHeight={calculatedHeight}
            _textColor={textColor}
          >
            <PlaylistContent>
              {items.map(
                (item, id) =>
                  item && <PlaylistItem data={item} key={id} id={id} />
              )}
            </PlaylistContent>
          </ScrollContainer>
        </Container>

        {inBackground && <StyledFreeLink />}
      </Component>
    );
  }
);

const Component = styled.div<{
  _showPlaylist: boolean;
  _isEmbedBackground: boolean;
  _hideDirection?: HideDirection;
}>`
  overflow: hidden;
  transition:
    ${getAnimation('opacity')}, ${getAnimation('transform')},
    ${getAnimation('color', 'ease', 0)};
  pointer-events: ${({ _showPlaylist }) => (_showPlaylist ? 'all' : 'none')};

  ${({ _showPlaylist, _isEmbedBackground }) => {
    if (_showPlaylist) {
      return;
    }

    if (_isEmbedBackground) {
      return css`
        background-color: transparent !important;
      `;
    }

    return css`
      opacity: 0;
    `;
  }}

  ${({ _hideDirection, _showPlaylist }) => {
    if (_hideDirection === 'toRight' && _showPlaylist) {
      return css`
        transform: translateX(0);
      `;
    }

    if (_hideDirection === 'toRight' && !_showPlaylist) {
      return css`
        transform: translateX(100%);
      `;
    }

    if (_hideDirection !== 'toRight') {
      return css`
        transform: translateX(0);
      `;
    }
  }}
`;

const Container = styled.div<{
  _showPlaylist: boolean;
  _hideDirection?: HideDirection;
}>`
  transition: ${getAnimation()};
  opacity: ${({ _showPlaylist }) => (_showPlaylist ? 1 : 0)};

  ${({ _hideDirection, _showPlaylist }) =>
    _hideDirection === 'toTop' &&
    !_showPlaylist &&
    css`
      height: 0;
    `}

  ${({ _hideDirection }) =>
    _hideDirection === 'toRight' &&
    css`
      height: 100%;
    `}

  ${({ _hideDirection }) =>
    _hideDirection !== 'toRight' &&
    _hideDirection !== 'toTop' &&
    css`
      height: auto;
    `}
`;

const SCROLLBAR_TRACK_SIZE = 16;

const ScrollContainer = styled(SimpleBar)<{
  _maxHeight: string | number;
  _textColor: string;
}>`
  ${({ _maxHeight }) => css({ maxHeight: _maxHeight })}

  /* Shopify conflict fix */
  & .simplebar-placeholder {
    display: block;
  }

  & .simplebar-content-wrapper {
    padding-right: ${SCROLLBAR_TRACK_SIZE}px;
  }

  & .simplebar-track.simplebar-vertical {
    width: ${SCROLLBAR_TRACK_SIZE}px;
    pointer-events: auto !important; /* Даем браузеру видеть мышь над дорожкой */
  }

  & .simplebar-track.simplebar-horizontal {
    display: none !important;
  }

  /* Чтобы нативный :hover работал - pointer-events: auto; */
  & .simplebar-scrollbar {
    min-height: 20px;
    pointer-events: auto !important;
  }

  & .simplebar-scrollbar::before {
    right: 0;
    left: ${SCROLLBAR_TRACK_SIZE / 2}px;
    border-radius: 4px;
    background-color: ${({ _textColor }) => getColorWithAlpha(_textColor, 0.2)};
    opacity: 1;
    transition: ${getAnimation('background-color')};
  }

  & .simplebar-scrollbar.simplebar-visible:hover::before,
  &
    .simplebar-track.simplebar-dragging
    .simplebar-scrollbar.simplebar-visible::before {
    background-color: ${({ _textColor }) => getColorWithAlpha(_textColor, 0.9)};
  }
`;

const PlaylistContent = styled.div.attrs({
  className: createReadableClass({
    component: 'playlist',
    element: 'content'
  })
})``;
